import { SwapOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EligibilityResult, Position } from '@shared/types';
import { api, isNoStandard } from '../api/client.js';
import type { ExportTable } from '../lib/exportTable.js';
import { levelLabel } from '../pages/PersonCard.js';
import { ExportButtons } from './ExportButtons.js';

/**
 * จำลองการโยกย้ายและแต่งตั้ง — เลือกอัตราว่างระดับสูง แล้วดูว่าใครเข้าข่ายบ้าง
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ถ้อยคำในหน้านี้ห้ามแก้ให้ "ขายง่ายขึ้น"
 * ────────────────────────────────────────────────────────────────
 *
 * ผู้ใช้ขอฟีเจอร์นี้ด้วยคำว่า "ระบบแนะนำคนที่เหมาะสมที่สุด" แต่หน้าจอนี้ต้องไม่พูดแบบนั้น
 * และนี่ไม่ใช่การเลี่ยงคำ — เป็นข้อจำกัดของตัวระบบเอง
 *
 *   1. สิ่งที่ระบบตรวจได้คือ "ระยะเวลาการดำรงตำแหน่ง" ตามมาตรฐานกำหนดตำแหน่งเท่านั้น
 *      ยังขาดผลงาน สมรรถนะ วิสัยทัศน์ และประวัติทางวินัย ซึ่งทั้งหมดอยู่นอกข้อมูลชุดนี้
 *      จึงต้องเขียนว่า "ผ่านเกณฑ์ระยะเวลา" ไม่ใช่ "มีคุณสมบัติครบถ้วน"
 *
 *   2. ลำดับที่แสดงคือ "ลำดับอาวุโส" ไม่ใช่ "ลำดับความเหมาะสม"
 *      ระบบราชการพลเรือนใช้ระบบคุณธรรม อาวุโสเป็นตัวตัดสินเฉพาะตอนคะแนนเสมอกัน
 *      ถ้าเขียนว่าอันดับ 1 คือคนที่เหมาะสมที่สุด หน้าจอนี้จะกลายเป็นเอกสารที่ใช้อ้างผิด
 *      ในกระบวนการที่มีผลต่ออาชีพของคนจริง
 *
 * กฎสองข้อนี้เป็นข้อเดียวกับที่เขียนกำกับไว้ใน pages/Eligibility.tsx มาก่อนแล้ว
 * ที่นี่ทำหน้าที่เหมือนกันแต่เข้าทางตำแหน่งว่างแทนที่จะเข้าทางระดับ
 */
export function RotationModal({ onClose }: { onClose: () => void }) {
  const [picked, setPicked] = useState<Position | null>(null);
  const [q, setQ] = useState('');
  const closeBtn = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  /** อัตราว่างระดับสูง — นิยามของคำว่า "ระดับสูง" มาจากเซิร์ฟเวอร์ ไม่ได้ตั้งซ้ำที่หน้าจอ */
  const vac = useQuery({ queryKey: ['vacancies', 'senior'], queryFn: () => api.vacancies('senior') });
  /** ระดับไหนมีมาตรฐานกำหนดตำแหน่งในระบบบ้าง — ใช้บอกล่วงหน้าว่าตำแหน่งไหนตรวจเกณฑ์ให้ไม่ได้ */
  const std = useQuery({ queryKey: ['standards'], queryFn: api.standards });

  /**
   * ป้าย "ยังไม่มีเกณฑ์ในระบบ" ข้างชื่อตำแหน่งในบัญชีซ้าย
   *
   * ⚠ นี่คือ "การเดา" ของหน้าจอ ไม่ใช่คำตอบจริง — คำตอบจริงมาจาก standardFor() ฝั่งเซิร์ฟเวอร์
   *   ที่ต้องเดาเพราะบัญชีซ้ายมี 183 แถว จะยิงถามเซิร์ฟเวอร์ทีละแถวไม่ไหว
   *
   * เดาผิดได้ในทางที่ไม่เป็นอันตราย เพราะแผงขวาไม่ได้เชื่อค่านี้ — มันยิงถามเซิร์ฟเวอร์เสมอ
   * แล้วแสดงเหตุผลที่เซิร์ฟเวอร์ตอบกลับมา ป้ายนี้จึงมีผลแค่กับข้อความเล็ก ๆ ในบัญชี
   *
   * ⚠ ห้ามเอาค่านี้กลับไปใช้ปิดการเรียก API อีก (เดิมเป็นแบบนั้น) — ระดับที่ใช้เอกสาร
   *   คนละฉบับต่อสายงานจะถูกนับว่า "มีเกณฑ์" จากระดับอย่างเดียว แล้วยิง API โดยไม่มีสายงาน
   *   ได้ 404 ขึ้นข้อความสีแดงเต็มจอ ทั้งที่คำตอบที่ถูกคือ "ยังไม่มีฉบับของสายงานนี้"
   */
  const ruleHint = useMemo(() => {
    const anyLine = new Set<string>();
    const perLine = new Set<string>();
    for (const s of std.data?.available ?? []) {
      if (s.appliesToLines === 'all') anyLine.add(s.dpisLevel);
      else for (const l of s.appliesToLines) perLine.add(`${s.dpisLevel}|${l.trim()}`);
    }
    return (level: string, line: string) =>
      anyLine.has(level) || perLine.has(`${level}|${line.trim()}`);
  }, [std.data]);

  const items = useMemo(() => {
    const all = vac.data?.items ?? [];
    const key = q.trim();
    if (!key) return all;
    return all.filter(
      (p) => p.title.includes(key) || p.unit.includes(key) || p.level.includes(key),
    );
  }, [vac.data, q]);

  /**
   * ส่งออก "รายการอัตราว่างระดับสูง" — ตรงกับแผงซ้าย ไม่ใช่รายชื่อผู้เข้าข่าย
   *
   * ⚠ หน้าต่างนี้มีปุ่มส่งออกสองจุด และแต่ละจุดส่งออกคนละตาราง
   * ปุ่มบนหัว = รายการอัตราว่าง (สิ่งที่หัวหน้าต่างพูดถึง)
   * ปุ่มในแผงขวา = รายชื่อผู้เข้าข่ายของอัตราที่เลือก
   *
   * ที่ไม่ทำปุ่มเดียวแล้วให้มัน "เดา" ว่าผู้ใช้อยากได้อันไหน เพราะเดาผิดเมื่อไร
   * ผู้ใช้จะได้ไฟล์ผิดโดยไม่รู้ตัว แล้วเอาไปใช้ต่อ — ปุ่มที่อยู่ติดกับของที่มันส่งออก
   * ไม่มีทางเข้าใจผิดได้
   */
  const buildVacancies = useCallback((): ExportTable => ({
    title: 'อัตราว่างระดับสูง',
    subtitle: `${items.length} อัตรา${q.trim() ? ` · ค้นหา "${q.trim()}"` : ''}`,
    warnings: [
      'รายการนี้คืออัตราว่างในระดับที่ถือว่าสำคัญ (บริหาร อำนวยการ ทรงคุณวุฒิ เชี่ยวชาญ) ' +
        'ไม่ใช่อัตราว่างทั้งหมดของกระทรวง',
      'คอลัมน์ "มีเกณฑ์ในระบบ" บอกแค่ว่าระบบมีเอกสารมาตรฐานกำหนดตำแหน่งของระดับนั้นหรือยัง ' +
        'ไม่ได้แปลว่าอัตรานั้นมีหรือไม่มีผู้เข้าข่าย',
    ],
    columns: [
      { key: 'posNo', label: 'เลขที่ตำแหน่ง', width: 15 },
      { key: 'title', label: 'ชื่อตำแหน่ง', width: 34 },
      { key: 'level', label: 'ระดับ', width: 20 },
      { key: 'unit', label: 'หน่วยงาน', width: 36 },
      { key: 'months', label: 'ว่างมาแล้ว (เดือน)', width: 16, numeric: true },
      { key: 'rule', label: 'มีเกณฑ์ในระบบ', width: 14 },
    ],
    rows: items.map((p) => ({
      posNo: p.posNo,
      title: p.title,
      level: levelLabel(p.level),
      unit: p.unit,
      months: p.vacantMonths,
      rule: ruleHint(p.level, p.line) ? 'มี' : 'ยังไม่มี',
    })),
  }), [items, q, ruleHint]);

  return (
    <div
      className="pmodal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pmodal is-wide" role="dialog" aria-modal="true" aria-label="การเลื่อนขึ้นตำแหน่งว่างระดับสูง">
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><SwapOutlined /></span>
          <div>
            <span className="kind">การเลื่อนขึ้นตำแหน่งว่างระดับสูง</span>
            <b>ตำแหน่งว่างระดับสูง {vac.data ? `${vac.data.shown} อัตรา` : ''}</b>
            <span className="sub">
              เลือกตำแหน่งจากบัญชี เพื่อดูผู้ผ่านเกณฑ์ระยะเวลาการดำรงตำแหน่งตามมาตรฐานกำหนดตำแหน่ง
            </span>
          </div>
          {vac.data && <ExportButtons build={buildVacancies} />}
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        <div className="simbody">
          <aside className="simlist">
            <div className="simsearch">
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ค้นหาชื่อตำแหน่ง ส่วนราชการ หรือระดับ"
                aria-label="ค้นหาตำแหน่งว่าง"
              />
            </div>

            {vac.isPending && <p className="state sm">กำลังโหลดบัญชีตำแหน่งว่าง…</p>}
            {vac.isError && <p className="state error sm">{String(vac.error)}</p>}

            {vac.data && items.length === 0 && (
              <p className="state sm">ไม่พบตำแหน่งว่างที่ตรงกับคำค้น</p>
            )}

            <ul>
              {items.map((p, i) => (
                <li key={`${p.posNo ?? 'no'}-${i}`}>
                  <button
                    type="button"
                    aria-pressed={picked === p}
                    onClick={() => setPicked(p)}
                  >
                    <b>{p.title}</b>
                    <span className="lv">
                      {levelLabel(p.level)}
                      {!ruleHint(p.level, p.line) && <em> · ยังไม่มีเกณฑ์ในระบบ</em>}
                    </span>
                    <span className="un">{p.unit}</span>
                    {p.vacantMonths !== null && (
                      <span className="du">ว่างมาแล้ว {duration(p.vacantMonths)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="simmain">
            {picked ? (
              <Candidates position={picked} />
            ) : (
              <div className="simempty">
                <SwapOutlined aria-hidden="true" />
                <b>เลือกตำแหน่งว่างจากบัญชีด้านซ้าย</b>
                <p>
                  ระบบจะตรวจว่าใครผ่านเกณฑ์ระยะเวลาการดำรงตำแหน่งตามมาตรฐานกำหนดตำแหน่งของระดับนั้น
                  แล้วเรียงตามลำดับอาวุโสตามหลักเกณฑ์ ก.พ.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- ฝั่งขวา: ผู้เข้าข่ายของอัตราที่เลือก ---------------- */

function Candidates({ position }: { position: Position }) {
  /* ⚠ ต้องส่ง position.line ไปด้วยเสมอ และต้องอยู่ใน queryKey ด้วย
     ระดับเดียวกันคนละสายงานใช้เอกสารคนละฉบับ ถ้า key มีแต่ระดับ
     คำตอบของสายงานแรกที่กดจะถูกแคชแล้วเอาไปตอบสายงานอื่นทั้งหมด */
  const report = useQuery({
    queryKey: ['eligibility', position.level, position.line],
    queryFn: () => api.eligibility(position.level, position.line),
  });

  /* เซิร์ฟเวอร์เป็นผู้ตัดสินว่ามีมาตรฐานให้ใช้หรือไม่ ไม่ใช่หน้าจอเดาเอง */
  const missing = report.data && isNoStandard(report.data) ? report.data.reason : null;
  const data = report.data && !isNoStandard(report.data) ? report.data : null;

  /**
   * ส่งออกรายชื่อผู้ผ่านเกณฑ์ระยะเวลาของอัตราที่เลือกอยู่
   *
   * ⚠ คำเตือนสามข้อแรกต้องอยู่ในไฟล์เสมอ ห้ามตัดออกให้ไฟล์ดูสะอาด
   *
   * ไฟล์นี้คือ "รายชื่อคนเรียงลำดับ" ซึ่งเป็นรูปแบบเดียวกับเอกสารเสนอชื่อ
   * พอถูกพิมพ์หรือส่งต่อ ไม่มีอะไรติดไปบอกว่าลำดับที่ 1 ไม่ได้แปลว่าเหมาะสมที่สุด
   * ซึ่งเป็นความเข้าใจผิดที่มีผลต่ออาชีพของคนจริง
   */
  const buildCandidates = useCallback((): ExportTable => {
    const d = data;
    return {
      title: `ผู้ผ่านเกณฑ์ระยะเวลา ${position.posNo ?? ''} ${position.title}`.trim(),
      subtitle:
        `${position.title} · ${levelLabel(position.level)}` +
        `${position.posNo ? ` · เลขที่ ${position.posNo}` : ''}` +
        ` · ${position.orgPath.join(' > ') || position.unit}`,
      warnings: [
        'ลำดับในไฟล์นี้คือลำดับอาวุโสตามหลักเกณฑ์ ก.พ. ไม่ใช่ลำดับความเหมาะสม ' +
          'และใช้ตัดสินเฉพาะเมื่อคณะกรรมการให้คะแนนเสมอกันทุกประการ',
        'ระบบตรวจได้เฉพาะระยะเวลาการดำรงตำแหน่งตามมาตรฐานกำหนดตำแหน่ง ' +
          'ยังไม่รวมผลงาน สมรรถนะ วิสัยทัศน์ และประวัติทางวินัย ซึ่งอยู่นอกข้อมูลชุดนี้',
        'กฎ ก.พ. ว่าด้วยการย้าย การโอน หรือการเลื่อนข้าราชการพลเรือนสามัญ พ.ศ. 2564 ข้อ 3 ' +
          'กำหนดให้พิจารณาโดยคำนึงถึงศักยภาพ ผลสัมฤทธิ์ของงาน สมรรถนะ ประวัติการรับราชการ ' +
          'ความรู้ความสามารถ ตลอดจนพฤติกรรมทางจริยธรรมและความประพฤติ',
        ...(d?.warnings ?? []),
      ],
      columns: [
        { key: 'rank', label: 'ลำดับอาวุโส', width: 12, numeric: true },
        { key: 'name', label: 'ชื่อ-สกุล', width: 26 },
        { key: 'title', label: 'ตำแหน่งปัจจุบัน', width: 30 },
        { key: 'level', label: 'ระดับปัจจุบัน', width: 20 },
        { key: 'unit', label: 'หน่วยงาน', width: 36 },
        { key: 'paths', label: 'ผ่านเกณฑ์ข้อ', width: 16 },
        { key: 'manual', label: 'ข้อที่ต้องให้เจ้าหน้าที่ยืนยัน', width: 20, numeric: true },
        { key: 'since', label: 'ดำรงระดับปัจจุบันตั้งแต่', width: 18 },
        { key: 'retire', label: 'เกษียณในอีก (เดือน)', width: 16, numeric: true },
      ],
      rows: (d?.candidates ?? []).map((c) => ({
        rank: c.seniorityRank,
        name: c.name,
        title: c.currentTitle,
        level: levelLabel(c.currentLevel),
        unit: c.unit,
        paths: c.passedPaths.join(', '),
        manual: c.pendingManualChecks.length,
        since: c.currentLevelSince,
        retire: c.retiresWithinMonths,
      })),
    };
  }, [data, position]);

  return (
    <>
      <section className="pmodal-sec">
        <h4>ตำแหน่งที่พิจารณา</h4>
        <div className="simtarget">
          <b>{position.title}</b>
          <span>
            {levelLabel(position.level)}
            {position.posNo && ` · เลขที่ ${position.posNo}`}
          </span>
          <span className="un">{position.orgPath.join(' › ') || position.unit}</span>
          {position.vacantMonths !== null && (
            <span className="du">
              ว่างมาแล้ว {duration(position.vacantMonths)}
              {position.vacantSince && ` (ตั้งแต่ ${thaiDate(position.vacantSince)})`}
            </span>
          )}
          {/* ⚠ คำถามแรกที่ผู้บริหารถามเมื่อเห็นอัตราที่ว่างค้างหลายปีคือ "ว่างเพราะอะไร"
              เพราะอัตราที่ว่างเพราะเกษียณกับว่างเพราะโอนออก มีทางแก้คนละแบบ */}
          {position.lastHolder && (
            <span className="lh">
              ผู้ครองคนก่อน {position.lastHolder.name}
              {position.lastHolder.reason && ` · ${position.lastHolder.reason}`}
            </span>
          )}
        </div>
      </section>

      {/* ⚠ ข้อความนี้มาจากเซิร์ฟเวอร์ ไม่ใช่หน้าจอเขียนเอง
          เพราะเซิร์ฟเวอร์แยกได้ว่า "ระดับนี้ยังไม่มีเอกสารเลย" ต่างจาก
          "ระดับนี้มีเอกสารแล้ว แต่ยังไม่มีฉบับของสายงานนี้" ซึ่งต้องไปขอเอกสารคนละชุดกัน
          ถ้าหน้าจอเขียนเองจะได้ข้อความเดียวคลุมทั้งสองกรณีเหมือนเดิม */}
      {missing && (
        <p className="pmodal-note">
          {missing} — ไม่ได้แปลว่าไม่มีผู้เข้าข่าย
        </p>
      )}

      {report.isPending && <p className="state">กำลังตรวจระยะเวลาการดำรงตำแหน่งของผู้เข้าข่าย…</p>}
      {report.isError && <p className="state error">{String(report.error)}</p>}

      {data && (
        <>
          <section className="pmodal-sec">
            <h4>มาตรฐานกำหนดตำแหน่งที่ใช้ตรวจ</h4>
            <p className="simrule">
              <b>{data.standard.source.title}</b>
              {data.standard.source.ref && <span> · {data.standard.source.ref}</span>}
              {/* ⚠ เดิมมีข้อความกำกับว่าเป็นเกณฑ์จำลอง / ยังไม่ได้สอบทาน — ผู้ใช้สั่งเอาออก
                  ฟิลด์ source.synthetic กับ source.verified ยังส่งมาจากเซิร์ฟเวอร์เหมือนเดิม */}
            </p>
            <ul className="simpaths">
              {data.standard.paths.map((path) => (
                <li key={path.id}>
                  <span className="id">ข้อ {path.id}</span>
                  {path.label}
                </li>
              ))}
            </ul>
          </section>

          <div className="simtally">
            <div>
              <span className="n">{data.passed}</span>
              <span className="l">ผ่านเกณฑ์ระยะเวลาการดำรงตำแหน่ง</span>
            </div>
            <div>
              <span className="n muted">{data.noHistory}</span>
              <span className="l">ตรวจไม่ได้ เนื่องจากไม่มีประวัติในระบบ</span>
            </div>
            <div>
              <span className="n muted">{data.unreadableHistory}</span>
              <span className="l">ประวัติมีชื่อระดับที่ระบบไม่รู้จัก</span>
            </div>
          </div>

          {/*
            คำเตือนนี้ไม่ใช่ของประดับ และห้ามย้ายไปไว้ท้ายรายการ
            มันต้องอยู่ "ก่อน" รายชื่อ เพราะคนที่เปิดหน้านี้มาเพื่อหาคนขึ้นตำแหน่ง
            จะอ่านรายชื่อก่อนเสมอ แล้วอันดับ 1 จะกลายเป็นข้อสรุปในหัวทันทีถ้าไม่ได้เตือนไว้ก่อน
          */}
          <p className="simwarn">
            <b>รายชื่อนี้ไม่ใช่ลำดับความเหมาะสม</b>
            สิ่งที่ระบบตรวจได้คือระยะเวลาการดำรงตำแหน่งตามมาตรฐานกำหนดตำแหน่งเท่านั้น
            ยังไม่รวมผลงาน สมรรถนะ วิสัยทัศน์ และประวัติทางวินัย ซึ่งอยู่นอกข้อมูลชุดนี้
            ส่วนลำดับที่เห็นคือลำดับอาวุโสตามหลักเกณฑ์ ก.พ. ซึ่งใช้ตัดสินเฉพาะเมื่อคะแนนเสมอกัน
          </p>

          {data.warnings.length > 0 && (
            <ul className="simissues">
              {data.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          <section className="pmodal-sec">
            <h4>
              ผู้ผ่านเกณฑ์ระยะเวลา
              <span>เรียงตามลำดับอาวุโส</span>
            </h4>
            {/* ปุ่มอยู่ติดกับรายชื่อที่มันส่งออก จึงไม่มีทางเข้าใจผิดว่าจะได้ตารางไหน */}
            <div className="vc-tools">
              <ExportButtons build={buildCandidates} />
            </div>
            {data.candidates.length === 0 ? (
              <p className="state">ไม่มีผู้ผ่านเกณฑ์ระยะเวลาการดำรงตำแหน่งสำหรับระดับนี้</p>
            ) : (
              <ol className="simcands">
                {data.candidates.map((c) => (
                  <Candidate key={`${c.personaId ?? c.name}-${c.seniorityRank}`} c={c} />
                ))}
              </ol>
            )}
          </section>

          <section className="pmodal-sec">
            <h4>หลักเกณฑ์การจัดลำดับอาวุโสที่ใช้</h4>
            <ol className="simsteps">
              {data.seniorityRule.steps.map((st) => (
                <li key={st.order}>{st.label}</li>
              ))}
            </ol>
            <p className="simsrc">{data.seniorityRule.source.title}</p>
          </section>
        </>
      )}
    </>
  );
}

function Candidate({ c }: { c: EligibilityResult }) {
  return (
    <li className={c.meetsTimeRequirement ? undefined : 'is-off'}>
      <span className="rank">{c.seniorityRank}</span>
      <div className="who">
        <b>{c.name}</b>
        <span className="pos">
          {c.currentTitle} · {levelLabel(c.currentLevel)}
        </span>
        <span className="un">{c.unit}</span>

        <span className="tags">
          {c.passedPaths.length > 0 && (
            <span className="tag ok">ผ่านเกณฑ์ข้อ {c.passedPaths.join(', ')}</span>
          )}
          {c.currentLevelSince && (
            <span className="tag">ขึ้นระดับปัจจุบัน {thaiDate(c.currentLevelSince)}</span>
          )}
          {c.decidedByStep !== null && (
            <span className="tag">ตัดสินกับคนก่อนหน้าด้วยขั้นที่ {c.decidedByStep}</span>
          )}
          {/* เกษียณเร็วกว่ากระบวนการแต่งตั้งจะเสร็จ — เป็นเรื่องที่ต้องเห็นก่อนเสนอชื่อ */}
          {c.retiresWithinMonths !== null && (
            <span className="tag is-warn">เกษียณในอีก {duration(c.retiresWithinMonths)}</span>
          )}
        </span>

        {c.pendingManualChecks.length > 0 && (
          <p className="manual">
            เงื่อนไขที่ระบบตรวจแทนไม่ได้ ต้องให้เจ้าหน้าที่ยืนยัน:{' '}
            {c.pendingManualChecks.map((m) => m.label).join(' · ')}
          </p>
        )}
      </div>
    </li>
  );
}

/* ---------------- ตัวช่วย ---------------- */

const TH_MONTH = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(d)} ${TH_MONTH[Number(m) - 1]} ${Number(y) + 543}`;
}

function duration(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (!y) return `${m} เดือน`;
  return m ? `${y} ปี ${m} เดือน` : `${y} ปี`;
}
